import React, { useEffect, useState } from "react";
import { Tab, Tabs, NavLink } from "../../components";
import { makeStyles } from "@material-ui/styles";
import sharedStyles from "../styles";
import { useLocation, useHistory } from "react-router-dom";
import { Alert } from "@material-ui/lab";

const useStyles = makeStyles(sharedStyles);

export default function SearchTabs({ permissions }) {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [accessDenied, setAccessDenied] = useState(false);

  // Define the tab paths and check if they're enabled
  const tabConfig = [
    {
      path: "/search",
      label: "Workflows",
      isEnabled:
        permissions?.workflows?.active === "True" &&
        permissions?.workflows?.hide === "False",
    },
    {
      path: "/search/tasks",
      label: "Tasks",
      isEnabled:
        permissions?.tasks?.active === "True" &&
        permissions?.tasks?.hide === "False",
    },
  ];

  // Filter only enabled tabs
  const enabledTabs = React.useMemo(
    () => tabConfig.filter((tab) => tab.isEnabled),
    [permissions]
  );

  const redirectToFirstEnabledTab = () => {
    if (enabledTabs.length > 0) {
      history.replace(enabledTabs[0].path);
    } else {
      history.replace("/no-access");
    }
  };

  const handleNoEnabledTabs = (currentPath) => {
    if (enabledTabs.length === 0 && !currentPath.includes("/no-access")) {
      history.replace("/no-access");
      return true;
    }
    return false;
  };

  // Helper function to find the matching tab index
  const findMatchingTabIndex = (currentPath) => {
    return tabConfig.findIndex(
      (tab) =>
        currentPath === tab.path ||
        (tab.path === "/search" && currentPath === "/search/") ||
        (tab.path === "/search" &&
          currentPath.startsWith("/search") &&
          !currentPath.startsWith("/search/tasks"))
    );
  };

  const handleTabPermissions = (matchingTab) => {
    if (!matchingTab.isEnabled) {
      setAccessDenied(true);
      redirectToFirstEnabledTab();
      return false;
    }
    setAccessDenied(false);
    return true;
  };

  // Handle navigation and permissions
  useEffect(() => {
    const currentPath = location.pathname;

    // Check if there are no enabled tabs
    if (handleNoEnabledTabs(currentPath)) {
      return;
    }

    // Find if current path matches any tab
    const matchingTabIndex = findMatchingTabIndex(currentPath);

    // If we found a matching tab
    if (matchingTabIndex >= 0) {
      const matchingTab = tabConfig[matchingTabIndex];

      // Check if user has permission for this tab
      if (!handleTabPermissions(matchingTab)) {
        return;
      }

      // Find the index in the filtered enabledTabs array
      const enabledTabIndex = enabledTabs.findIndex(
        (tab) => tab.path === matchingTab.path
      );
      setActiveTabIndex(enabledTabIndex);
    } else {
      // If current path doesn't match any tab, redirect to first enabled tab
      redirectToFirstEnabledTab();
    }
  }, [location.pathname, tabConfig, enabledTabs, history]);

  // If there are no enabled tabs, don't render anything
  if (enabledTabs.length === 0) {
    return null;
  }

  return (
    <>
      {accessDenied && (
        <Alert severity="warning" style={{ margin: "10px" }}>
          You don't have permission to access this page. Redirecting...
        </Alert>
      )}

      <Tabs
        value={activeTabIndex}
        className={classes.tabHeader}
        textColor="primary"
      >
        {enabledTabs.map((tab, index) => (
          <Tab
            key={tab.path}
            label={tab.label}
            component={NavLink}
            path={tab.path}
          />
        ))}
      </Tabs>
    </>
  );
}
